import React, { useCallback, useEffect, useState } from 'react';
import { logger } from '@gov-nx/api';
import { useIdentity } from '@gov-nx/auth/common';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	usePersonCommunicationLoader,
	useServiceListLoader,
	useUserSettingsLoader,
} from '@gov-nx/core/hooks';
import { useGlobalLocale } from '@gov-nx/core/service';
import { Time } from '@gov-nx/core/types';
import {
	useDataBoxLoader,
	useDataBoxUnreadMessages,
} from '@gov-nx/module/data-box';
import { isDifferentTimeZone } from '@gov-nx/utils/common';

export interface SyncProviderProps {
	children: React.ReactNode;
	enableServicesList?: boolean;
}

export function SyncProvider({
	children,
	enableServicesList = false,
}: SyncProviderProps) {
	const [processing, setProcessing] = useState(true);

	const { t } = useGlobalLocale();
	const { toastMessage } = useMessageEvents();

	const { isAuthenticated } = useIdentity();

	const { initDataBoxes } = useDataBoxLoader();
	const { initDataBoxUnreadMessages, refetchDataBoxUnreadMessages } =
		useDataBoxUnreadMessages();
	const { initPersonCommunication } = usePersonCommunicationLoader();
	const { initUserSettings } = useUserSettingsLoader();
	const { initServicesList } = useServiceListLoader();

	const init = useCallback(async () => {
		logger('SyncProvider -> init', []);
		const promises = [
			initDataBoxes(),
			initPersonCommunication(),
			initUserSettings(),
		];

		if (enableServicesList) {
			promises.push(initServicesList());
		}

		return await Promise.all(promises);
	}, [
		initDataBoxes,
		initPersonCommunication,
		initServicesList,
		initUserSettings,
	]);

	useEffect(() => {
		if (isAuthenticated) {
			init().finally(() => {
				setProcessing(false);
				initDataBoxUnreadMessages().catch();
				isDifferentTimeZone() &&
					toastMessage({
						content: t('zprava.informace-casova-zona'),
						options: {
							color: 'warning',
							type: 'bold',
							time: Time['1 minute'],
							gravity: 'bottom',
							position: 'center',
						},
					});
			});
		} else {
			setProcessing(false);
		}

		// Note: Updating deps causing infinite loop here !
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated]);

	useEffect(() => {
		if (isAuthenticated) {
			const refetchInterval = setInterval(() => {
				refetchDataBoxUnreadMessages();
			}, Time['30 seconds']);

			return () => clearInterval(refetchInterval);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated]);

	if (processing) {
		return null;
	}

	// eslint-disable-next-line react/jsx-no-useless-fragment
	return <>{children}</>;
}
